<div class="space-y-4">
    <div class="text-sm text-gray-500 leading-relaxed">
        เลือก platform ที่ผู้ใช้แต่ละคนสามารถเข้าถึงได้
        <span class="block">ผู้ใช้ที่ยังไม่ได้รับ assign จะไม่เห็น platform ใดเลย</span>
    </div>

    @if (loading) {
    <div class="flex flex-col items-center py-6 space-y-3">
        <app-loader></app-loader>
        <div class="font-medium">Loading</div>
    </div>
    } @else {

    <!-- Toolbar: select all + bulk action -->
    <div class="flex items-center gap-3 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg">
        <label class="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" [checked]="isAllUsersSelected()"
                [indeterminate]="isSomeUsersSelected()" (change)="toggleAllUsers()">
            <span>เลือกทั้งหมด</span>
        </label>
        @if (selectedUserIds.size > 0) {
        <span class="text-sm text-primary-600 font-medium">
            ({{selectedUserIds.size}} คนถูกเลือก)
        </span>
        <button class="text-xs text-gray-500 hover:text-gray-900 ml-auto"
            (click)="clearBulkSelection()">ยกเลิกการเลือก</button>
        }
    </div>

    <!-- Bulk action panel -->
    @if (selectedUserIds.size > 0) {
    <div class="border-2 border-primary-400 bg-primary-50/40 rounded-lg p-4 space-y-3">
        <div class="text-sm font-semibold text-primary-700">
            กำหนด platform ให้ {{selectedUserIds.size}} user พร้อมกัน
        </div>
        <mat-form-field class="w-full" subscriptSizing="dynamic">
            <mat-select multiple [value]="bulkPendingIds"
                (selectionChange)="onBulkSelectionChange($event.value)" [disabled]="bulkSaving"
                placeholder="เลือก platform ที่จะ apply ให้ทุกคน">
                @for (group of channelGroups; track group.platform) {
                <mat-optgroup
                    [label]="group.platform + ' (' + groupSelectedCount(group, bulkPendingIds) + '/' + group.channels.length + ')'">
                    <mat-option [value]="groupMarkerValue(group)">
                        เลือกทั้งหมด
                    </mat-option>
                    @for (channel of group.channels; track channel.platform_id) {
                    <mat-option [value]="channel.platform_id">
                        {{channel.platform_name}}
                    </mat-option>
                    }
                </mat-optgroup>
                }
            </mat-select>
        </mat-form-field>
        <div class="flex justify-end gap-2">
            <button class="btn-sm whitespace-nowrap text-gray-600" [disabled]="bulkSaving"
                (click)="clearBulkSelection()">ยกเลิก</button>
            <button class="btn-sm btn-primary whitespace-nowrap" [disabled]="bulkSaving"
                (click)="applyBulk()">
                Apply ให้ {{selectedUserIds.size}} user
            </button>
        </div>
    </div>
    }

    <div class="flex flex-col gap-3">
        @for (user of userDatasets; track user.user_id) {
        <div class="border rounded-lg p-4 transition-colors"
            [ngClass]="hasChanges(user) ? 'border-primary-400 bg-primary-50/40' : (isUserSelected(user.user_id) ? 'border-primary-300 bg-primary-50/20' : 'border-gray-200 bg-white')">

            <!-- Header: checkbox + avatar + name + actions -->
            <div class="flex flex-row gap-3 items-center mb-3">
                <input type="checkbox" class="flex-none" [checked]="isUserSelected(user.user_id)"
                    (change)="toggleUserSelected(user.user_id)">
                <img class="w-10 h-10 rounded-full flex-none object-cover bg-gray-100"
                    [src]="user.image || 'assets/images/user.png'"
                    onerror="this.onerror=null; this.src='assets/images/user.png'" alt="user">
                <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2 flex-wrap">
                        <div class="font-semibold truncate">{{user.name}}</div>
                        @if (user.role) {
                        <span class="label label-primary !text-xs">{{permissionService.getRole(user.role)}}</span>
                        } @else {
                        <span class="label label-light !text-xs border">ยังไม่มี role</span>
                        }
                    </div>
                </div>
                <div class="flex-none flex gap-2 items-center text-sm">
                    <span class="text-gray-500 whitespace-nowrap">
                        {{totalSelected(user)}}/{{totalChannels()}} platforms
                    </span>
                    @if (hasChanges(user)) {
                    <button class="text-xs text-gray-500 hover:text-gray-900" (click)="resetChanges(user)">
                        ยกเลิก
                    </button>
                    }
                    <button class="btn-sm btn-primary whitespace-nowrap"
                        [disabled]="saving[user.user_id] || !hasChanges(user)"
                        (click)="savePlatforms(user)">บันทึก</button>
                </div>
            </div>

            <!-- Selector -->
            <mat-form-field class="w-full" subscriptSizing="dynamic">
                <mat-select multiple [value]="user.pending_ids"
                    (selectionChange)="onSelectionChange(user, $event.value)" [disabled]="saving[user.user_id]"
                    placeholder="คลิกเพื่อเลือก platform">
                    @for (group of channelGroups; track group.platform) {
                    <mat-optgroup
                        [label]="group.platform + ' (' + groupSelectedCount(group, user.pending_ids) + '/' + group.channels.length + ')'">
                        <mat-option [value]="groupMarkerValue(group)">
                            เลือกทั้งหมด
                        </mat-option>
                        @for (channel of group.channels; track channel.platform_id) {
                        <mat-option [value]="channel.platform_id">
                            {{channel.platform_name}}
                        </mat-option>
                        }
                    </mat-optgroup>
                    }
                </mat-select>
            </mat-form-field>

            <!-- Selected summary grouped by platform -->
            @if (user.platform_ids.length === 0) {
            <div class="mt-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                ยังไม่กำหนด — ผู้ใช้คนนี้จะไม่เห็น platform ใดเลย
            </div>
            } @else {
            <div class="mt-3 space-y-2">
                @for (group of channelGroups; track group.platform) {
                @if (groupSelectedCount(group, user.platform_ids) > 0) {
                <div class="flex items-start gap-3 text-sm">
                    <span class="text-xs font-semibold text-gray-600 w-20 flex-none mt-1">
                        {{group.platform}}
                        <span class="text-gray-400 font-normal">
                            ({{groupSelectedCount(group, user.platform_ids)}}/{{group.channels.length}})
                        </span>
                    </span>
                    <div class="flex flex-row gap-1 flex-wrap">
                        @for (channel of getSelectedChannelsInGroup(user, group); track channel.platform_id) {
                        <div class="label label-secondary !text-xs text-nowrap">
                            {{channel.platform_name}}
                        </div>
                        }
                    </div>
                </div>
                }
                }
            </div>
            }
        </div>
        }
    </div>
    }
</div>
